import { Logo } from "@/components/Logo";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset. Please log in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="rd-hero-sheen absolute inset-0 h-[40vh] pointer-events-none" />
      <div className="relative flex-1 flex flex-col justify-center px-6 max-w-[440px] mx-auto w-full">
        <div className="mb-8">
          <Logo className="h-20 w-20 rounded-2xl rd-glow mb-4" />
          <h1 className="font-display text-3xl font-bold leading-tight">
            REAL DEAL <span className="rd-lime">FITNESS</span>
          </h1>
        </div>

        {!token ? (
          <div data-testid="reset-invalid" className="rounded-2xl bg-card border border-border p-6 text-center">
            <div className="h-12 w-12 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto mb-3"><ShieldAlert className="h-6 w-6 text-red-300" /></div>
            <h2 className="font-display text-xl font-bold">Invalid reset link</h2>
            <p className="text-muted-foreground text-sm mt-2">This link is missing its token. Please request a new reset link.</p>
            <Button data-testid="reset-goto-login" onClick={() => navigate("/auth")} className="w-full mt-5 h-11 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Back to log in</Button>
          </div>
        ) : done ? (
          <div data-testid="reset-done" className="rounded-2xl bg-card border border-border p-6 text-center">
            <div className="h-12 w-12 rounded-2xl bg-[#ccff0c]/15 flex items-center justify-center mx-auto mb-3"><CheckCircle2 className="h-6 w-6 text-[#ccff0c]" /></div>
            <h2 className="font-display text-xl font-bold">Password updated</h2>
            <p className="text-muted-foreground text-sm mt-2">You can now log in with your new password.</p>
            <Button data-testid="reset-login" onClick={() => navigate("/auth")} className="w-full mt-5 h-11 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Log in</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div>
              <h2 className="font-display text-xl font-bold">Set a new password</h2>
              <p className="text-muted-foreground text-sm mt-1">Choose a strong password you'll remember.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-pw">New password</Label>
              <Input data-testid="reset-password" id="new-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="bg-card border-border h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-pw">Confirm password</Label>
              <Input data-testid="reset-confirm" id="confirm-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" className="bg-card border-border h-11" />
            </div>
            <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold text-base">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reset password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
